import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Printer, AlertTriangle, FileText, Download } from 'lucide-react';
import api from '@/utils/api';

/**
 * Bundle 66 - Print Badges modal.
 *
 * Props:
 *   open, onClose
 *   eventId (string)
 *   selectedIds (array of registration ids currently selected on the
 *     Registrations page); if empty we fall through to
 *     `filteredIds` (all rows currently visible after filters).
 *   filteredIds (array; the rows the operator can currently see).
 */
const BadgePrintDialog = ({ open, onClose, eventId, selectedIds, filteredIds }) => {
  const [layout, setLayout] = useState('individual');
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const registrationIds = selectedIds && selectedIds.length > 0
    ? selectedIds
    : (filteredIds || []);
  const scope = selectedIds && selectedIds.length > 0
    ? 'Selected registrations'
    : 'Current filtered results';

  const loadPlan = useCallback(async () => {
    if (!eventId || !open) return;
    setPlanLoading(true);
    setPlanError(null);
    try {
      const { data } = await api.post(
        `/admin/events/${eventId}/badges/plan`,
        { registration_ids: registrationIds },
      );
      setPlan(data);
      if (data.missing_event_fields?.length) {
        setPlanError({
          kind: 'event_fields_missing',
          missing: data.missing_event_fields,
        });
      }
    } catch (e) {
      setPlanError({
        kind: 'plan_failed',
        message: e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Plan failed',
      });
    } finally { setPlanLoading(false); }
  }, [eventId, open, registrationIds]);

  const loadPreview = useCallback(async () => {
    if (!eventId || !open) return;
    setPreviewLoading(true);
    try {
      const resp = await api.post(
        `/admin/events/${eventId}/badges/preview`,
        { registration_ids: registrationIds },
        { responseType: 'blob' },
      );
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (_) {
      // silent; the main plan endpoint surfaces the operator-facing error
    } finally { setPreviewLoading(false); }
  }, [eventId, open, registrationIds]);

  useEffect(() => {
    if (!open) return;
    loadPlan();
    loadPreview();
  }, [open, loadPlan, loadPreview]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const doGenerate = useCallback(async () => {
    setGenerating(true);
    try {
      const resp = await api.post(
        `/admin/events/${eventId}/badges/generate`,
        { layout, registration_ids: registrationIds },
        { responseType: 'blob' },
      );
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const cd = resp.headers?.['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/i);
      const filename = m ? m[1] : `dostination-badges-${layout}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
      onClose?.();
    } catch (e) {
      const d = e?.response?.data;
      const msg = d?.detail?.message || d?.detail || 'Generate failed';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally { setGenerating(false); }
  }, [eventId, layout, registrationIds, onClose]);

  const canGenerate = plan && (plan.eligible_count || 0) > 0
    && !(plan.missing_event_fields?.length);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-4xl" data-testid="badge-print-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="h-5 w-5" /> Print event badges
          </DialogTitle>
          <DialogDescription>
            Vector PDF at exact 4.25 x 3.25 in. Print at 100% or Actual
            Size. Do not select Fit to Page.
          </DialogDescription>
        </DialogHeader>

        {planLoading && (
          <div className="text-sm text-neutral-500 py-2 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Planning badges...
          </div>
        )}

        {planError?.kind === 'event_fields_missing' && (
          <div
            className="rounded-md border border-accent-300 bg-accent-50 p-3 text-sm text-accent-900 flex items-start gap-2"
            data-testid="badge-print-event-fields-missing"
          >
            <AlertTriangle className="h-4 w-4 mt-0.5" />
            <div>
              <div className="font-semibold">Event is missing required fields</div>
              <ul className="list-disc pl-4 mt-1 text-xs">
                {planError.missing.map((m) => (<li key={m}>{m}</li>))}
              </ul>
              <div className="mt-1 text-xs">
                Fill these on the event settings page then reopen this dialog.
              </div>
            </div>
          </div>
        )}

        {plan && !planError && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="badge-print-plan">
            <div className="space-y-3">
              <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-sm space-y-1">
                <div><span className="font-semibold">Event:</span> {plan.event?.title}</div>
                <div><span className="font-semibold">Location:</span> {plan.event?.city}</div>
                <div><span className="font-semibold">Date:</span> {plan.event?.date_formatted}</div>
                <div className="text-xs text-neutral-500">
                  Timezone: {plan.event?.timezone || 'UTC'}
                </div>
              </div>

              <div className="text-sm">
                <div className="text-neutral-600">Scope</div>
                <div className="font-semibold">{scope}</div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="bg-success-50 text-success-800" data-testid="badge-print-eligible-chip">
                  {plan.eligible_count} will be generated
                </Badge>
                {plan.excluded_count > 0 && (
                  <Badge variant="outline" className="bg-accent-50 text-accent-800" data-testid="badge-print-excluded-chip">
                    {plan.excluded_count} excluded
                  </Badge>
                )}
              </div>

              {plan.excluded_count > 0 && (
                <div className="rounded-md border border-accent-200 bg-accent-50/40 p-3 text-xs" data-testid="badge-print-excluded-list">
                  <div className="font-semibold text-accent-900 mb-1 flex items-center gap-1">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Excluded from this PDF
                  </div>
                  <ul className="space-y-1 text-neutral-800 max-h-32 overflow-y-auto">
                    {plan.excluded.map((row) => (
                      <li key={row.registration_id} className="flex justify-between gap-2">
                        <span className="truncate">{row.display_hint || row.email || row.registration_id}</span>
                        <span className="text-neutral-500 whitespace-nowrap">{row.reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="space-y-1">
                <label className="text-xs font-semibold text-neutral-600">Layout</label>
                <Select value={layout} onValueChange={setLayout}>
                  <SelectTrigger className="w-full" data-testid="badge-print-layout-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual (one badge per 4.25 x 3.25 in page)</SelectItem>
                    <SelectItem value="letter">US Letter, 6 badges per page (2 x 3 grid, 8.5 x 11 in)</SelectItem>
                    <SelectItem value="tabloid">Tabloid, 6 badges per page (11 x 17 in)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">Preview</div>
              <div
                className="rounded-md border border-neutral-200 bg-white overflow-hidden aspect-[4.25/3.25]"
                data-testid="badge-print-preview-frame"
              >
                {previewLoading ? (
                  <div className="h-full w-full flex items-center justify-center text-neutral-500 text-sm">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Rendering...
                  </div>
                ) : previewUrl ? (
                  <object
                    data={previewUrl}
                    type="application/pdf"
                    className="h-full w-full"
                  >
                    <div className="p-4 text-sm text-neutral-500 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Preview not viewable inline. <a href={previewUrl} target="_blank" rel="noreferrer" className="text-primary-800 underline">Open PDF</a>
                    </div>
                  </object>
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-neutral-400 text-sm">
                    No preview available
                  </div>
                )}
              </div>
              <div className="text-[11px] text-neutral-500">
                Preview uses the first eligible registration. Actual PDF
                personalizes each badge with the attendee&apos;s first name.
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="mt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={doGenerate}
            disabled={!canGenerate || generating}
            data-testid="badge-print-generate-btn"
          >
            {generating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}
            Generate PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BadgePrintDialog;
